import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Checkpoint { key: string; label: string; location: string; at: string | null; reached: boolean; current: boolean }
interface Tracking {
  reference: string; method: string; carrier: string; carrierLabel: string; eta: string;
  handoff: string; receipt: string; trackingNumber?: string; trackingUrl?: string;
  state: string; checkpoints: Checkpoint[]; estimatedDelivery?: string | null;
}

const CARRIER_ICON: Record<string, string> = { InPost: '📦', 'Royal Mail': '📮', DPD: '🚚' };

function fmtDate(iso?: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Souqli delivery tracking - carrier-style timeline with ETA + granular checkpoints. */
export function TrackingModal({ orderId, title, onClose }: { orderId: string; title?: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<Tracking>({
    queryKey: ['tracking', orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}/tracking`)).data,
  });

  const done = data?.state === 'RELEASED' || data?.state === 'COLLECTED';
  const reached = data?.checkpoints.filter((c) => c.reached).length ?? 0;
  const total = data?.checkpoints.length ?? 1;
  const pct = Math.round((reached / total) * 100);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card p-0 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="section-dark relative bg-ink text-cream p-5 overflow-hidden">
          <div className="absolute inset-0 bg-gold-mesh opacity-40" />
          <div className="absolute inset-0 dotgrid-light bg-[length:20px_20px] opacity-20" />
          <div className="relative flex items-start justify-between">
            <div className="min-w-0">
              <div className="stamp text-gold text-[9px]">TRACKING</div>
              <h2 className="display text-2xl mt-1 leading-tight truncate">{title ?? data?.reference ?? 'Your parcel'}</h2>
              {data && (
                <div className="text-[12px] text-cream/70 mt-1 flex items-center gap-1.5">
                  <span>{CARRIER_ICON[data.carrier] ?? '📦'}</span>{data.carrierLabel}
                  {data.trackingNumber && <span className="mono text-cream/50">· {data.trackingNumber}</span>}
                </div>
              )}
            </div>
            <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full bg-cream/10 hover:bg-cream/20 grid place-items-center text-cream text-lg shrink-0">×</button>
          </div>
        </div>

        <div className="p-6">
          {isLoading || !data ? (
            <div className="space-y-3">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-8 skeleton rounded" />)}</div>
          ) : (
            <>
              {/* ETA banner */}
              <div className={`rounded-luxe p-4 flex items-center gap-4 ${done ? 'bg-ok/10 border border-ok/30' : 'bg-gold/8 border border-gold/30'}`}>
                <div className="text-3xl">{done ? '✅' : '🚚'}</div>
                <div>
                  {done ? (
                    <>
                      <div className="stamp text-ok text-[9px]">DELIVERED</div>
                      <div className="display text-xl text-ink leading-tight">Your item has arrived</div>
                    </>
                  ) : (
                    <>
                      <div className="stamp text-gold-dark text-[9px]">ESTIMATED {data.method === 'locker' ? 'READY' : 'DELIVERY'}</div>
                      <div className="display text-xl text-ink leading-tight">{fmtDate(data.estimatedDelivery) ?? `In ${data.eta}`}</div>
                    </>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-4">
                <div className="h-2 bg-cream-deep rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${done ? 'bg-ok' : 'bg-gold'}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex justify-between stamp text-mute text-[9px] mt-1.5"><span>ORDERED</span><span>{data.method === 'locker' ? 'COLLECTED' : 'DELIVERED'}</span></div>
              </div>

              {/* Granular checkpoints */}
              <ol className="relative border-l-2 border-warmline ml-2 mt-6 space-y-5">
                {data.checkpoints.map((c) => (
                  <li key={c.key} className="ml-5 relative">
                    <span className={`absolute -left-[26px] top-0 w-4 h-4 rounded-full grid place-items-center text-[9px] ${
                      c.reached ? 'bg-ok text-cream' : c.current ? 'bg-gold text-ink animate-pulse' : 'bg-cream-deep border border-warmline'
                    }`}>{c.reached ? '✓' : ''}</span>
                    <div className={`text-[13px] ${c.current ? 'font-semibold text-ink' : c.reached ? 'text-ink' : 'text-mute'}`}>{c.label}</div>
                    <div className="text-[11px] text-mute">{c.location}{c.at && <span className="mono"> · {fmtTime(c.at)}</span>}</div>
                  </li>
                ))}
              </ol>

              {/* Sender / recipient */}
              <div className="mt-6 grid grid-cols-2 gap-3 text-[11px] text-ink-700">
                <div className="rounded-luxe border border-warmline p-3">
                  <div className="stamp text-gold-dark text-[9px] mb-1">SELLER SENDS</div>{data.handoff}
                </div>
                <div className="rounded-luxe border border-warmline p-3">
                  <div className="stamp text-gold-dark text-[9px] mb-1">BUYER GETS</div>{data.receipt}
                </div>
              </div>

              {data.trackingNumber && (
                <a href={`/track/${data.trackingNumber}`} target="_blank" rel="noreferrer" className="btn btn-primary w-full mt-4 text-sm">Open full tracking page ↗</a>
              )}
              {data.trackingUrl && (
                <a href={data.trackingUrl} target="_blank" rel="noreferrer" className="btn btn-outline w-full mt-2 text-sm">Carrier tracking ↗</a>
              )}
              <p className="stamp text-mute text-[9px] text-center mt-3">FUNDS HELD SAFELY UNTIL YOU CONFIRM COLLECTION</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
